import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTour } from '../context/TourContext';
import './SpotlightTour.css';

const PADDING = 12;
const TOOLTIP_W = 320;
const TOOLTIP_H_ESTIMATE = 220;

const MOBILE_BREAKPOINT = 1000;

function resolveTarget(step) {
  if (step.mobileTarget && window.innerWidth < MOBILE_BREAKPOINT) return step.mobileTarget;
  return step.target;
}

function findTarget(target) {
  return document.querySelector(`[data-tour="${target}"]`);
}

// Optional steps (step.optional) whose target is not rendered are skipped — e.g. a section that only exists
// with data. Counted at render time, so the counter only shows the steps the user actually gets to see.
function isStepAvailable(step) {
  return !step.optional || !!findTarget(resolveTarget(step));
}

function computeRect(target) {
  const el = document.querySelector(`[data-tour="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - PADDING,
    left: r.left - PADDING,
    width: r.width + PADDING * 2,
    height: r.height + PADDING * 2,
    bottom: r.bottom + PADDING,
    right: r.right + PADDING,
  };
}

function tooltipPosition(rect) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tw = Math.min(TOOLTIP_W, vw - 24);
  const clampLeft = (l) => Math.min(Math.max(l, 12), vw - tw - 12);

  if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' };

  const clampTop = (t) => Math.max(12, Math.min(t, vh - TOOLTIP_H_ESTIMATE - 12));

  // Below
  if (rect.bottom + TOOLTIP_H_ESTIMATE + 16 < vh) {
    return { top: rect.bottom + 16, left: clampLeft(rect.left) };
  }
  // Above
  if (rect.top - TOOLTIP_H_ESTIMATE - 16 > 0) {
    return { top: rect.top - TOOLTIP_H_ESTIMATE - 16, left: clampLeft(rect.left) };
  }
  // Right
  if (rect.right + 16 + tw <= vw) {
    return { top: clampTop(rect.top), left: rect.right + 16 };
  }
  // Left
  if (rect.left - 16 - tw >= 0) {
    return { top: clampTop(rect.top), left: rect.left - 16 - tw };
  }
  // Fallback: center
  return { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' };
}

export default function SpotlightTour() {
  const { activeTour, stepIndex, nextStep, prevStep, closeTour } = useTour();
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);
  // Own frame for resize/scroll: it must not cancel the step frame of updateRect (which skips missing steps).
  const resizeRafRef = useRef(null);
  const scrollTimerRef = useRef(null);
  // Direction of the last step change — a skipped optional step continues that way. Tracked during render
  // (idempotent, so a repeated render keeps the direction).
  const prevIndexRef = useRef(stepIndex);
  const backwardsRef = useRef(false);
  if (prevIndexRef.current !== stepIndex) {
    backwardsRef.current = stepIndex < prevIndexRef.current;
    prevIndexRef.current = stepIndex;
  }

  const updateRect = useCallback(() => {
    if (!activeTour) return;
    const step = activeTour.steps[stepIndex];
    if (!step) return;
    cancelAnimationFrame(rafRef.current);
    clearTimeout(scrollTimerRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const target = resolveTarget(step);
      const el = findTarget(target);
      if (!el && step.optional) {
        // Going back from the first available steps there is nothing before: continue forward instead.
        if (backwardsRef.current && stepIndex > 0) prevStep();
        else nextStep();
        return;
      }
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        scrollTimerRef.current = setTimeout(() => setRect(computeRect(target)), 400);
      } else {
        setRect(computeRect(target));
      }
    });
  }, [activeTour, stepIndex, nextStep, prevStep]);

  useEffect(() => {
    updateRect();
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(scrollTimerRef.current);
    };
  }, [updateRect]);

  useEffect(() => {
    const onResize = () => {
      cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = requestAnimationFrame(() => {
        if (!activeTour) return;
        const step = activeTour.steps[stepIndex];
        if (step) setRect(computeRect(resolveTarget(step)));
      });
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      cancelAnimationFrame(resizeRafRef.current);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [activeTour, stepIndex]);

  if (!activeTour) return null;

  const step = activeTour.steps[stepIndex];
  const available = activeTour.steps.map(isStepAvailable);
  const totalSteps = available.filter(Boolean).length;
  const stepNumber = available.slice(0, stepIndex + 1).filter(Boolean).length;
  const isLast = !available.slice(stepIndex + 1).some(Boolean);
  // Current step is being skipped (optional, target missing): keep the backdrop, show no tooltip.
  const skipping = !available[stepIndex];
  const tooltipPos = tooltipPosition(rect);

  return (
    <AnimatePresence>
      <div className="spotlight-root" key={`tour-${activeTour.key}-${stepIndex}`} onClick={closeTour}>

        {rect && !skipping && (
          <div
            className="spotlight-hole"
            style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
          />
        )}

        {!skipping && (
          <motion.div
            className="spotlight-tooltip"
            style={{ top: tooltipPos.top, left: tooltipPos.left, transform: tooltipPos.transform }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="spotlight-tooltip-header">
              <span className="spotlight-step-counter">{Math.max(stepNumber, 1)} / {Math.max(totalSteps, 1)}</span>
              <button type="button" className="spotlight-close-btn" onClick={closeTour}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <h3 className="spotlight-title">{step.title}</h3>
            <p className="spotlight-description">{step.description}</p>

            <div className="spotlight-actions">
              {stepIndex > 0 && (
                <button type="button" className="spotlight-btn secondary" onClick={prevStep}>
                  <span className="material-symbols-rounded">arrow_back</span>
                  Zurück
                </button>
              )}
              <button type="button" className="spotlight-btn primary" onClick={nextStep}>
                {isLast ? 'Fertig' : 'Weiter'}
                {!isLast && <span className="material-symbols-rounded">arrow_forward</span>}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </AnimatePresence>
  );
}
